'use client';

import {
  useWriteContract,
  useSimulateContract,
  useWaitForTransactionReceipt,
  UseSimulateContractParameters,
  usePublicClient,
} from 'wagmi';
import { Button } from './PrimaryButton';
import { ReactNode, useCallback } from 'react';
import { useDeepMemo } from '../useDeepMemo';
import { toast } from 'react-toastify';
import { useCreatePromise } from '../useCreatePromise';
import { wait } from '@latticexyz/common/utils';
import { afterUnless } from '../afterUnless';
import { waitForTransactionReceipt } from 'viem/actions';
import { ViewTransaction } from './ViewTransaction';

export type Props = {
  label: ReactNode;
  write: UseSimulateContractParameters;
  className?: string;
};

export function WriteButton({ write: writeArgs, label, className }: Props) {
  const publicClient = usePublicClient({ chainId: writeArgs.chainId });
  const memoizedWriteArgs = useDeepMemo(writeArgs);
  const { data: simulateResult, isError: simulateError } = useSimulateContract(memoizedWriteArgs);
  const {
    data: hash,
    writeContractAsync,
    isPending: writePending,
    error: writeError,
  } = useWriteContract();
  const { isLoading: txPending, error: txError } = useWaitForTransactionReceipt({
    chainId: memoizedWriteArgs.chainId,
    hash,
  });
  const isLoading = Boolean(!simulateResult || writePending || txPending);
  const isError = Boolean(simulateError || writeError || txError);

  const [, writeAndConfirm] = useCreatePromise(
    useCallback(
      async (onProgress: (message: ReactNode) => void) => {
        if (!writeContractAsync || !publicClient || !simulateResult) {
          throw new Error('Contract call could not be prepared');
        }

        onProgress('Starting transaction…');

        const writePromise = writeContractAsync(simulateResult.request);
        void afterUnless(wait(5000), writePromise, () =>
          onProgress('Please confirm transaction in your wallet…'),
        );
        const txHash = await writePromise;

        onProgress('Finalizing transaction…');

        const receiptPromise = waitForTransactionReceipt(publicClient, {
          hash: txHash,
        });

        void afterUnless(wait(15000), receiptPromise, () =>
          onProgress('It can sometimes take a while to finalize a transaction…'),
        );
        void afterUnless(wait(30000), receiptPromise, () => onProgress('Still working on it…'));
        const receipt = await receiptPromise;

        if (receipt.status === 'reverted') {
          throw Error('Transaction reverted');
        }

        return { hash: txHash, receipt };
      },
      [simulateResult, publicClient, writeContractAsync],
    ),
  );

  if (writePending) {
    return <Button className={className} label={label} pending />;
  }

  if (txPending) {
    return (
      <>
        <Button className={className} label={label} pending />
        <ViewTransaction hash={hash} />
      </>
    );
  }

  if (isError) {
    // TODO: display better message for specific kinds of errors (eth balance, etc)
    return <Button className={className} label="Could not estimate gas" disabled />;
  }

  return (
    <>
      <Button
        className={className}
        label={label}
        pending={isLoading}
        onClick={(event) => {
          event.preventDefault();

          const toastId = toast.loading('Preparing…');
          writeAndConfirm((message: ReactNode) => toast.update(toastId, { render: message })).then(
            () => {
              toast.update(toastId, {
                isLoading: false,
                type: 'success',
                render: 'Success!',
                autoClose: 15000,
                closeButton: true,
              });
            },
            (error) => {
              toast.update(toastId, {
                isLoading: false,
                type: 'error',
                render: error.shortMessage ? (
                  error.shortMessage
                ) : (
                  <div className="whitespace-pre-wrap">{String(error)}</div>
                ),
                autoClose: 15000,
                closeButton: true,
              });
            },
          );
        }}
      />

      <ViewTransaction hash={hash} />
    </>
  );
}
